import './settings-leaf.css';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { exchangeRateApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, Select, TextInput } from '../ui.tsx';
import { currencyOptions } from '../../forms/currencies.ts';
import { formatShortDate } from '../../i18n/datetime.ts';
import type { ExchangeRate, ExchangeRatesResponse } from '@shared/types/index.ts';
import { normalizeTypedRate } from '@shared/exchange-rates.ts';

/**
 * Exchange rates (Settings › Company, migration 0115).
 *
 * One rate per currency the company deals in besides its own — a location's
 * own currency appears here by itself — always as "1 EUR = 1.0834 USD". A rate
 * is typed by hand or follows the daily reference rate. They convert reports
 * into the company currency and money received in another currency; a card
 * is always charged in the currency of what it pays, so no charge depends on
 * them.
 */
export default function ExchangeRatesCard() {
  const { t } = useTranslation();
  const { can, company } = useSession();
  const queryClient = useQueryClient();
  const canWrite = can('company:write');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  // A rate typed for the currency being added: the daily feed does not
  // publish every currency, and one it does not still has to be addable.
  const [addingRate, setAddingRate] = useState('');
  // The company currency the draft above was typed against. A draft typed
  // against another (the company currency changed beside this card) is not
  // offered, let alone saved, against the new one.
  const [addingBase, setAddingBase] = useState('');

  // Keyed by the company currency every rate is against, so changing it in
  // the card beside this one reads the rates again rather than showing (and
  // offering to save) rates against the old one.
  const queryKey = ['exchange-rates', company?.currencyCode ?? null];
  const rates = useQuery({ queryKey, queryFn: () => exchangeRateApi.list() });
  const settle = (data: ExchangeRatesResponse) => {
    setError(null);
    queryClient.setQueryData(['exchange-rates', data.baseCurrencyCode], data);
  };
  const fail = (caught: unknown) =>
    setError(caught instanceof ApiRequestError ? caught.message : t('exchangeRates.saveError'));

  const refresh = useMutation({ mutationFn: () => exchangeRateApi.refresh(), onSuccess: settle, onError: fail });
  const follow = useMutation({
    mutationFn: (code: string) => exchangeRateApi.set(code, { source: 'automatic' }),
    onSuccess: (data) => {
      settle(data);
      setAdding('');
      setAddingRate('');
    },
    onError: fail,
  });
  const addManual = useMutation({
    mutationFn: (code: string) =>
      exchangeRateApi.set(code, {
        source: 'manual',
        rate: normalizeTypedRate(addingRate),
        baseCurrencyCode: addingBase,
      }),
    onSuccess: (data) => {
      settle(data);
      setAdding('');
      setAddingRate('');
    },
    onError: fail,
  });

  const data = rates.data;
  if (!data) return null;
  const draftCode = addingBase === data.baseCurrencyCode ? adding : '';
  const draftRate = draftCode ? addingRate : '';
  const listed = new Set([data.baseCurrencyCode, ...data.rates.map((rate) => rate.currencyCode)]);
  const addable = currencyOptions(data.baseCurrencyCode).filter((option) => !listed.has(option.value));

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="exchange-rates-heading">
      <div className="work-section-heading">
        <h2 id="exchange-rates-heading" className="mb-2 text-lg font-semibold">
          {t('exchangeRates.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        {t('exchangeRates.description', { currency: data.baseCurrencyCode })}
      </p>
      {error ? (
        <Alert tone="error" title={t('exchangeRates.saveError')}>
          <p>{error}</p>
        </Alert>
      ) : null}

      {data.rates.length === 0 ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('exchangeRates.empty')}</p>
      ) : (
        <ul className="mb-4 grid gap-3">
          {data.rates.map((rate) => (
            <RateRow
              // A new base is a new row: a rate typed against the old one is
              // not carried over into the new one's editor.
              key={`${data.baseCurrencyCode}:${rate.currencyCode}`}
              rate={rate}
              base={data.baseCurrencyCode}
              canWrite={canWrite}
              onSaved={settle}
              onFailed={fail}
            />
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label={t('exchangeRates.addLabel')}
            value={draftCode}
            options={[{ value: '', label: t('exchangeRates.addPlaceholder') }, ...addable]}
            onChange={(event) => {
              setAdding(event.target.value);
              setAddingBase(data.baseCurrencyCode);
              // A rate typed for one currency is not the next one's.
              setAddingRate('');
            }}
          />
          {draftCode ? (
            <TextInput
              label={t('exchangeRates.rateLabel', { from: draftCode, to: data.baseCurrencyCode })}
              inputMode="decimal"
              value={draftRate}
              onChange={(event) => setAddingRate(event.target.value)}
            />
          ) : null}
          <Button
            disabled={!draftCode || !draftRate.trim() || addManual.isPending}
            onClick={() => addManual.mutate(draftCode)}
          >
            {t('exchangeRates.addManual')}
          </Button>
          <Button disabled={!draftCode || follow.isPending} onClick={() => follow.mutate(draftCode)}>
            {t('exchangeRates.addFollow')}
          </Button>
          {data.rates.some((rate) => rate.source === 'automatic') ? (
            <Button disabled={refresh.isPending} onClick={() => refresh.mutate()}>
              {t('exchangeRates.refresh')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function RateRow({
  rate,
  base,
  canWrite,
  onSaved,
  onFailed,
}: {
  rate: ExchangeRate;
  base: string;
  canWrite: boolean;
  onSaved: (data: ExchangeRatesResponse) => void;
  onFailed: (caught: unknown) => void;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState(rate.source === 'manual' ? (rate.rate ?? '') : '');
  const save = useMutation({
    mutationFn: () =>
      exchangeRateApi.set(rate.currencyCode, {
        source: 'manual',
        rate: normalizeTypedRate(typed),
        baseCurrencyCode: base,
      }),
    onSuccess: onSaved,
    onError: onFailed,
  });
  const follow = useMutation({
    mutationFn: () => exchangeRateApi.set(rate.currencyCode, { source: 'automatic' }),
    onSuccess: onSaved,
    onError: onFailed,
  });
  const remove = useMutation({
    mutationFn: () => exchangeRateApi.remove(rate.currencyCode),
    onSuccess: onSaved,
    onError: onFailed,
  });

  const current = rate.rate
    ? t('exchangeRates.current', { from: rate.currencyCode, rate: rate.rate, to: base })
    : t('exchangeRates.none', { from: rate.currencyCode });
  const how =
    rate.source === 'automatic'
      ? t('exchangeRates.sourceAutomatic', { date: rate.asOf ? formatShortDate(rate.asOf) : '—' })
      : rate.source === 'manual'
        ? t('exchangeRates.sourceManual')
        : null;

  return (
    <li className="rounded-md border border-[color:var(--color-line)] p-3">
      <p className="font-semibold">{current}</p>
      {how ? <p className="text-sm text-[color:var(--color-muted)]">{how}</p> : null}
      {canWrite ? (
        <div className="mt-2 flex flex-wrap items-end gap-3">
          <TextInput
            label={t('exchangeRates.rateLabel', { from: rate.currencyCode, to: base })}
            inputMode="decimal"
            value={typed}
            placeholder={rate.rate ?? '1.0'}
            onChange={(event) => setTyped(event.target.value)}
          />
          <Button disabled={!typed.trim() || save.isPending} onClick={() => save.mutate()}>
            {t('exchangeRates.saveManual')}
          </Button>
          {rate.source !== 'automatic' ? (
            <Button disabled={follow.isPending} onClick={() => follow.mutate()}>
              {t('exchangeRates.follow')}
            </Button>
          ) : null}
          {rate.rate ? (
            <Button disabled={remove.isPending} onClick={() => remove.mutate()}>
              {t('exchangeRates.remove')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
